import React from 'react';

import { TWO_FACTOR_SIGN_IN } from '@personal-trainer-app/shared';

import type { TwoFactorCodeFieldProps } from './TwoFactorCodeField.types';
import type { FormInputProps } from '@/components/forms';
import type { TwoFactorSignInMethod } from '@personal-trainer-app/shared';

import { FormInput } from '@/components/forms';

/**
 * Each field's keyboard and autofill: the number pad and the phone's code
 * suggestion for the app's code. A recovery code is mixed-case, so nothing
 * capitalises or corrects it.
 */
const FIELD = {
  code: { inputMode: 'numeric', autoComplete: 'one-time-code' },
  recovery_code: {
    autoComplete: 'off',
    autoCapitalize: 'none',
    autoCorrect: 'off',
    spellCheck: false,
  },
} as const satisfies Record<TwoFactorSignInMethod, Partial<FormInputProps>>;

/**
 * TwoFactorCodeField — the second factor, as the log-in's code step and
 * "Confirm it's you" both ask for it: the code from the authenticator app, or
 * a recovery code in its place (`TwoFactorMethodSwitch` swaps them). Keyed by
 * the method, so the other field mounts afresh when it swaps in.
 */
export function TwoFactorCodeField({
  method,
  value,
  onValueChange,
  error,
  helpText,
  autoFocus = false,
  disabled = false,
  onEnter,
}: TwoFactorCodeFieldProps): React.JSX.Element {
  const words = TWO_FACTOR_SIGN_IN[method];

  return (
    <FormInput
      key={method}
      label={words.label}
      placeholder={words.placeholder}
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      error={error ?? undefined}
      helpText={helpText}
      autoFocus={autoFocus}
      disabled={disabled}
      onKeyDown={
        onEnter
          ? (event) => {
              if (event.key === 'Enter') onEnter();
            }
          : undefined
      }
      {...FIELD[method]}
    />
  );
}

export default TwoFactorCodeField;
